import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import NewPostInput from './Post/NewPostInput';
import Post  from './Post/Post';
import PostReply from './Post/PostReply';
import './PlaygroundTab.css';
import { fetchPosts, ApiResponse, deletePost, createReplyPost } from '../../../api/playgroundServices';
import { UserType } from '../../../types/userType';
import LoadingScreen from '../../common/LoadingScreen/LoadingScreen';
import viLoadingLogo from '../../../assets/img/img_vi_loading_logo.png';
import { useNavigate } from 'react-router-dom';
import PostSkeleton from './Post/PostSkeleton';
import { usePlayground } from '../../../contexts/PlaygroundContext';
import { PostProps, ReplyPostData } from '../../../types/postType';

const STORAGE_KEY = 'playgroundPosts';
const MAX_STORAGE_AGE = 60 * 60 * 1000; // 1 hour in milliseconds

interface StoredPosts {
  posts: PostProps[];
  timestamp: number;
}

interface PlaygroundTabProps {
  onOpenModal: () => void;
  isActive: boolean;
  currentUser: UserType | null;
}

const PlaygroundTab: React.FC<PlaygroundTabProps> = ({ onOpenModal, isActive, currentUser }) => {
  const navigate = useNavigate();
  const { 
    cachedPosts, 
    setCachedPosts, 
    page, 
    setPage, 
    scrollPosition, 
    setScrollPosition 
  } = usePlayground();
  const [posts, setPosts] = useState<PostProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const hasScrolledRef = useRef(false);

  const postsRef = useRef<PostProps[]>([]);

  const [replyingToPost, setReplyingToPost] = useState<PostProps | null>(null);

  const loadPostsFromStorage = useCallback(() => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (storedData) {
      const { posts, timestamp }: StoredPosts = JSON.parse(storedData);
      const age = Date.now() - timestamp;
      if (age < MAX_STORAGE_AGE) {
        postsRef.current = posts;
        setPosts(postsRef.current);
        return true;
      }
    }
    return false;
  }, []);

  const savePostsToStorage = useCallback(() => {
    const dataToStore: StoredPosts = {
      posts: postsRef.current,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
  }, []);

  const loadPosts = useCallback(async (refresh = false) => {
    if (isLoading) return;
    
    if (!refresh && cachedPosts.length > 0) {
      setPosts(cachedPosts);
      postsRef.current = cachedPosts;
      return;
    }

    setIsLoading(true);
    try {
      const currentPage = refresh ? 1 : page;
      const response = await fetchPosts(currentPage);
      
      if (!response || !Array.isArray(response.results)) {
        throw new Error('Invalid response format');
      }

      const newPosts = response.results;
      
      if (refresh || currentPage === 1) {
        setCachedPosts(newPosts);
        setPosts(newPosts);
        postsRef.current = newPosts;
      } else {
        const updatedPosts = [...cachedPosts, ...newPosts];
        setCachedPosts(updatedPosts);
        setPosts(updatedPosts);
        postsRef.current = updatedPosts;
      }
      
      setPage(currentPage + 1);
      setHasMore(newPosts.length > 0);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again later.');
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [isLoading, page, cachedPosts, setCachedPosts, setPage]);

  const loadMorePosts = useCallback(() => {
    if (!isLoading && hasMore) {
      console.log('Loading more posts:', {
        currentPage: page,
        currentPostsCount: postsRef.current.length
      });
      loadPosts(false);
    }
  }, [loadPosts, isLoading, hasMore, page]);

  const handlePostDelete = useCallback((deletedPostId: number) => {
    postsRef.current = postsRef.current.filter(post => post.id !== deletedPostId);
    setPosts([...postsRef.current]);
    
    deletePost(deletedPostId)
      .then(() => {
        console.log('Post deleted successfully on the server');
      })
      .catch((error) => {
        console.error('Error deleting post on the server:', error);
        setError('An error occurred while deleting the post on the server. The post may reappear on refresh.');
      });
  }, []);

  const handleOpenReplyModal = useCallback((post: PostProps) => {
    setReplyingToPost(post);
  }, []);

  const handleCloseReplyModal = useCallback(() => {
    setReplyingToPost(null);
  }, []);

  useEffect(() => {
    if (isActive && isInitialLoad) {
      loadPosts();
      setIsInitialLoad(false);
    }
  }, [isActive, isInitialLoad, loadPosts]);

  const handleRefresh = useCallback(() => {
    console.log('Refreshing posts');
    setIsRefreshing(true);
    setPage(1);
    loadPosts(true);
  }, [loadPosts]);

  const handlePostClick = useCallback((postSlug: string) => {
    navigate(`/posts/${postSlug}`);
  }, [navigate]);

  const memoizedPosts = useMemo(() => {
    return posts.map((post) => (
      <Post 
        key={post.id}
        {...post}
        currentUser={currentUser}
        currentUserId={currentUser?.id?.toString() ?? ''}
        onPostDeleted={handlePostDelete}
        onReplyClick={() => handleOpenReplyModal(post)}
        onClick={() => handlePostClick(post.slug)}
      />
    ));
  }, [posts, currentUser?.id, handlePostDelete, handleOpenReplyModal, handlePostClick]);

  const handleSubmitReply = async (replyContent: string, imageFiles: File[], gif: string | null): Promise<void> => {
    if (!replyingToPost || !currentUser) return;

    try {
      const newReply: ReplyPostData = {
        id: Date.now(),
        content: replyContent,
        parentPostId: replyingToPost.id,
        user: {
          id: currentUser.id.toString(),
          username: currentUser.username,
          avatar: currentUser.avatar,
        },
        timestamp: new Date().toISOString(),
        postType: imageFiles.length > 0 ? 'media' : 'text',
        media: imageFiles.map(file => ({
          url: URL.createObjectURL(file),
          file: file,
          media_type: 'image',
          width: 0,
          height: 0
        })),
        isSelfPost: true,
        currentUser: currentUser,
        currentUserId: currentUser.id.toString(),
        isNewPost: true
      };

      await createReplyPost(newReply);
      handleCloseReplyModal();
      // Optionally refresh the posts after reply
      await loadPosts(true);
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  // Add debug log for scroll position changes
  useEffect(() => {
    console.log('Scroll position changed to:', scrollPosition);
  }, [scrollPosition]);

  // Add scroll event listener to debug scroll behavior
  useEffect(() => {
    const scrollableElement = document.getElementById('posts-container');
    
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      console.log('Scroll event:', {
        scrollTop: target.scrollTop,
        hasScrolledRef: hasScrolledRef.current,
        isActive
      });
    };

    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isActive]);

  // Debug tab switching scroll behavior
  useEffect(() => {
    const scrollableElement = document.getElementById('posts-container');
    
    if (!isActive && scrollableElement) {
      console.log('Tab deactivated, saving scroll position:', {
        position: scrollableElement.scrollTop,
        hasScrolledRef: hasScrolledRef.current
      });
      setScrollPosition(scrollableElement.scrollTop);
      hasScrolledRef.current = true;
    }
  }, [isActive]);

  useEffect(() => {
    const scrollableElement = document.getElementById('posts-container');
    
    if (isActive && hasScrolledRef.current && scrollableElement) {
      console.log('Tab activated, restoring scroll position:', {
        savedPosition: scrollPosition,
        currentPosition: scrollableElement.scrollTop,
        hasScrolledRef: hasScrolledRef.current
      });
      setTimeout(() => {
        if (scrollableElement) {
          scrollableElement.scrollTop = scrollPosition;
          console.log('Scroll position restored to:', scrollPosition);
        }
      }, 0);
    }
  }, [isActive, scrollPosition]);

  useEffect(() => {
    const scrollableElement = document.getElementById('posts-container');
    
    if (scrollableElement) {
      const observer = new MutationObserver((mutations) => {
        console.log('DOM mutation detected:', {
          mutations,
          currentScroll: scrollableElement.scrollTop
        });
      });

      observer.observe(scrollableElement, {
        childList: true,
        subtree: true,
        attributes: true
      });

      return () => observer.disconnect();
    }
  }, []);

  // Restore scroll position when component mounts
  useEffect(() => {
    if (isActive && scrollPosition > 0) {
      const scrollableElement = document.getElementById('posts-container');
      if (scrollableElement) {
        setTimeout(() => {
          scrollableElement.scrollTop = scrollPosition;
        }, 0);
      }
    }
  }, [isActive, scrollPosition]);

  // Save scroll position when navigating away
  useEffect(() => {
    return () => {
      const scrollableElement = document.getElementById('posts-container');
      if (scrollableElement) {
        setScrollPosition(scrollableElement.scrollTop);
      }
    };
  }, [setScrollPosition]);

  const LoadingIndicator = () => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <img 
        src={viLoadingLogo} 
        alt="Loading posts" 
        style={{ 
          width: '40px', 
          height: '40px',
          animation: 'spin 1s linear infinite'
        }} 
      />
    </div>
  );

  const handleOpenModal = useCallback(() => {
    if (currentUser) {
      onOpenModal();
    }
  }, [currentUser, onOpenModal]);

  if (!isActive) {
    return null;
  }

  return (
    <div className="playground-tab">
      <header className="playground-header">
        <h1>Playground</h1>
      </header>
      
      <div className="new-post-input-container">
        <NewPostInput 
          onOpenModal={handleOpenModal}
          currentUser={currentUser}
        />
      </div>
      
      <div 
        className="posts-container" 
        id="posts-container"
      >
        <InfiniteScroll
          dataLength={postsRef.current.length}
          next={loadMorePosts}
          hasMore={hasMore}
          loader={<LoadingIndicator />}
          height="100%"
          style={{ overflow: 'auto' }}
          endMessage={
            <p style={{ textAlign: 'center' }}>
              <b>No more posts to load.</b>
            </p>
          }
        >
          {isLoading && postsRef.current.length === 0 ? (
            <div>
              <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />  
            </div>
            
          ) : (
            memoizedPosts
          )}
        </InfiniteScroll>
      </div>
      {replyingToPost && (
        <PostReply
          isOpen={!!replyingToPost}
          onClose={handleCloseReplyModal}
          originalPost={replyingToPost}
          onSubmit={handleSubmitReply}
          currentUser={currentUser}
        />
      )}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default PlaygroundTab;
